import { useMemo, useState } from 'react'

import { useDownloadUsageBySiteExcel, useUsageBySiteApi } from '@/common/api/modules/billing'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'

// 直近12ヶ月の YYYY-MM を生成
const buildMonthOptions = (): string[] => {
  const out: string[] = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return out
}

const numCell = (v: number) => (
  <td className='border-primary border px-3 py-2 text-right tabular-nums'>{v.toLocaleString()}</td>
)

const ServiceUsageTab = () => {
  const monthOptions = useMemo(buildMonthOptions, [])
  const [month, setMonth] = useState(monthOptions[0])
  const { data, isLoading } = useUsageBySiteApi(month)

  // monthOptions は新しい月ほどインデックスが小さい（index 0 = 当月）
  const monthIndex = monthOptions.indexOf(month)
  const goNewer = () => {
    if (monthIndex > 0) setMonth(monthOptions[monthIndex - 1])
  }
  const goOlder = () => {
    if (monthIndex >= 0 && monthIndex < monthOptions.length - 1)
      setMonth(monthOptions[monthIndex + 1])
  }
  const canGoNewer = monthIndex > 0
  const canGoOlder = monthIndex >= 0 && monthIndex < monthOptions.length - 1

  const { trigger: downloadExcel, isMutating: isDownloading } = useDownloadUsageBySiteExcel(
    (blob) => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `サービス利用状況_${month}.xlsx`
      a.click()
    }
  )

  return (
    <Area>
      <div className='flex flex-col gap-4 p-2'>
        <div className='flex items-center gap-3'>
          <Text weight='bold'>対象月</Text>
          <button
            type='button'
            onClick={goOlder}
            disabled={!canGoOlder}
            aria-label='前の月'
            className='border-primary text-primary rounded-md border px-2.5 py-1.5 text-sm disabled:opacity-40'
          >
            ‹
          </button>
          <select
            className='border-primary rounded-md border px-3 py-1.5 text-sm'
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <button
            type='button'
            onClick={goNewer}
            disabled={!canGoNewer}
            aria-label='次の月'
            className='border-primary text-primary rounded-md border px-2.5 py-1.5 text-sm disabled:opacity-40'
          >
            ›
          </button>
          <Text size={12} color={TextColor.SECONDARY}>
            サイト単位の月次サービス利用量
          </Text>
          <div className='ml-auto'>
            <Button
              color={ButtonColor.SECONDARY}
              loading={isDownloading}
              onClick={() => downloadExcel({ month })}
            >
              Excelダウンロード
            </Button>
          </div>
        </div>

        {isLoading ? (
          <PageListSearching />
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse text-sm'>
              <thead>
                <tr className='bg-fill-surface-tertiary'>
                  <th
                    rowSpan={2}
                    className='border-primary border px-3 py-2 text-left align-bottom'
                  >
                    サイト
                  </th>
                  <th colSpan={3} className='border-primary border px-3 py-2'>
                    連動データ
                  </th>
                  <th colSpan={2} className='border-primary border px-3 py-2'>
                    Reco-Media
                  </th>
                  <th colSpan={3} className='border-primary border px-3 py-2'>
                    Media-DRIVER
                  </th>
                  <th className='border-primary border px-3 py-2'>GEO/LLMO</th>
                  <th className='border-primary border px-3 py-2'>UGC Intelligence</th>
                  <th colSpan={6} className='border-primary border px-3 py-2'>
                    Findee
                  </th>
                  <th className='border-primary border px-3 py-2'>カルテ</th>
                  <th colSpan={2} className='border-primary border px-3 py-2'>
                    AIペルソナ
                  </th>
                  <th colSpan={3} className='border-primary border px-3 py-2'>
                    商品意味理解
                  </th>
                </tr>
                <tr className='bg-fill-surface-tertiary'>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    ファイル取込み数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    データ保持量
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    MU（有効ユーザー）
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    タグデータ送信数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    レコメンド配信数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>記事数</th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    記事生成数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    アップロード数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    診断実行数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    レビュー投稿数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    エージェント起動回数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>会話数</th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    検索サジェスト使用回数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    有効タグ数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    タグ表示回数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    タグページ表示回数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    更新ユーザー数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    判定対象顧客数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    採用ペルソナ数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    処理商品数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    トークン数
                  </th>
                  <th className='border-primary border px-3 py-2 text-xs font-normal'>
                    概算費用(USD)
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.sites.map((s) => (
                  <tr key={s.site_id}>
                    <td className='border-primary border px-3 py-2 text-left'>
                      <Text>{s.site_name}</Text>
                    </td>
                    {numCell(s.data_integration)}
                    {numCell(s.data_retention)}
                    {numCell(s.mu)}
                    {numCell(s.reco_media_tag)}
                    {numCell(s.reco_media_delivery)}
                    {numCell(s.article_feature_pages)}
                    {numCell(s.article_create)}
                    {numCell(s.article_upload)}
                    {numCell(s.geo_llmo)}
                    {numCell(s.review)}
                    {numCell(s.ai_purchase_assist)}
                    {numCell(s.ai_purchase_conversation)}
                    {numCell(s.search_suggest_usage)}
                    {numCell(s.tag_active)}
                    {numCell(s.tag_impressions)}
                    {numCell(s.tag_page_views)}
                    {numCell(s.carte)}
                    {numCell(s.persona_judged_customers)}
                    {numCell(s.persona_adopted)}
                    {numCell(s.pu_products)}
                    {numCell(s.pu_tokens)}
                    {/* 金額は小数2桁。件数と同じ書式にすると 0.34 が 0 に見える */}
                    <td className='border-primary border px-3 py-2 text-right'>
                      <Text>{s.pu_cost_usd.toFixed(2)}</Text>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.sites.length === 0 && (
              <div className='py-8 text-center'>
                <Text color={TextColor.SECONDARY}>サイトがありません</Text>
              </div>
            )}
          </div>
        )}

        <Text size={12} color={TextColor.SECONDARY}>
          ※ 連動データ: ファイル取込み数 = 選択月のFTP取込件数。データ保持量 =
          現時点の各マスタ合計件数。 MU = ユーザーマスタの有効数（退会者を除く、現時点）。 ※
          タグデータ送信数 = タグから送信された行動イベント(閲覧/カート/購入)の件数。
          レコメンド配信数 = レコメンド配信ログの件数。 記事数 = 現時点の特集ページ数。記事生成数 =
          特集記事バージョンの生成数。アップロード数 = 記事アップロード申請の件数。 ※ Findee
          エージェント起動回数 =
          選択月の対話セッション数（配信画面の「エージェント起動回数」と同一指標）。 会話数 =
          質問と応答を1セットとしてカウントした選択月の件数（AIの応答メッセージ数）。 検索サジェスト使用回数 =
          選択月の検索実行回数（サジェスト経由の検索。クリック/CVは含まない）。 ※ Tag Trail
          有効タグ数 = 現時点の有効タグ数（統合・無効化を除く）。タグ表示回数 =
          選択月にタグ枠へ表示された回数。タグページ表示回数 =
          選択月のタグページ表示回数。 表示系2つは設置タグの実測のため、
          <strong>タグ未設置のサイトは 0 になる</strong>
          （有効タグ数が正で表示が0のときは、未計測を疑うこと）。 ※ AIペルソナ 判定対象顧客数 =
          現時点で有効な割当を持つ顧客数（割当は履歴を積むため、期間で数えると同じ顧客を重複計上する）。
          採用ペルソナ数 = 現時点の有効な採用数。いずれも選択月ではなく現時点のスナップショット。
        </Text>
      </div>
    </Area>
  )
}

export default ServiceUsageTab
